"use client";

import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Eye, RefreshCw, AlertCircle } from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/utils";
import { AppointmentStatus, Appointment } from "@/types";
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  startOfWeek,
  endOfWeek,
  format,
} from "date-fns";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import CommonHeader from "./common/CommonHeader";
import { AppointmentCalendarView } from "./appointment/AppointmentCalendarView";
import {
  AppointmentFilter,
  AppointmentFilters,
} from "./appointment/AppointmentFilter";
import { AppointmentsTable } from "./appointment/AppointmentsTable";
import { AppointmentDetailDialog } from "./appointment/AppointmentDetailDialog";
import { useDebounce } from "@/hooks/useDebounce";
import { AppointmentQueryParams } from "@/types/appointment.api.types";
import {
  getStatusBadgeClasses,
  getStatusLabel,
} from "@/lib/appointmentStatus";

// Convert filter state to API query parameters
const convertFiltersToParams = (
  filters: AppointmentFilters
): AppointmentQueryParams => {
  const params: AppointmentQueryParams = {};

  if (filters.status !== "all") {
    params.status = filters.status;
  }

  if (filters.customerId !== "all") {
    params.customerId = filters.customerId;
  }

  if (filters.staffId !== "all") {
    params.staffId = filters.staffId;
  }

  if (filters.startDate) {
    // Format as YYYY-MM-DD
    params.startDate = format(filters.startDate, "yyyy-MM-dd");
  }

  if (filters.endDate) {
    // Format as YYYY-MM-DD
    params.endDate = format(filters.endDate, "yyyy-MM-dd");
  }

  if (filters.search.trim()) {
    params.search = filters.search.trim();
  }

  return params;
};

export function AppointmentManagement() {
  const navigate = useNavigate();
  const {
    appointments,
    appointmentsLoading,
    appointmentsError,
    customers,
    vehicles,
    staff,
    updateAppointment,
    bulkAssignStaff,
    refreshAppointments,
    fetchAppointmentsWithFilters,
  } = useApp();

  const [activeTab, setActiveTab] = useState("list");
  const [selectedAppointment, setSelectedAppointment] =
    useState<Appointment | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Default filter: today to 3 days ahead
  const getDefaultDateFilter = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const threeDaysAhead = new Date(today);
    threeDaysAhead.setDate(today.getDate() + 3);
    threeDaysAhead.setHours(23, 59, 59, 999);
    return { startDate: today, endDate: threeDaysAhead };
  };

  // Derive showAllAppointments from filter state (no dates = show all)
  const defaultDates = getDefaultDateFilter();

  const [filters, setFilters] = useState<AppointmentFilters>({
    status: "all",
    search: "",
    customerId: "all",
    staffId: "all",
    startDate: undefined,
    endDate: undefined,
  });

  // Derive from filters - no dates means "show all" (syncs with manual date picks)
  const showAllAppointments = !filters.startDate && !filters.endDate;

  // Debounce search input to avoid excessive API calls
  const debouncedSearch = useDebounce(filters.search, 500);

  // Trigger API call when filters change
  useEffect(() => {
    const params = convertFiltersToParams({
      ...filters,
      search: debouncedSearch,
    });

    fetchAppointmentsWithFilters(params);
  }, [
    filters.status,
    filters.customerId,
    filters.staffId,
    filters.startDate,
    filters.endDate,
    debouncedSearch,
    fetchAppointmentsWithFilters,
  ]);

  // Calendar days - generate full weeks for proper grid alignment
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    // Get the start of the week containing the first day of the month (Sunday = 0)
    const start = startOfWeek(monthStart, { weekStartsOn: 0 });
    // Get the end of the week containing the last day of the month
    const end = endOfWeek(monthEnd, { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  const handleStatusUpdate = async (
    appointmentId: string,
    newStatus: AppointmentStatus
  ) => {
    try {
      // Pass the status directly - the appointmentService will handle mapping
      // updateAppointment shows the status-specific success toast
      await updateAppointment(appointmentId, { status: newStatus as any });
    } catch (error) {
      // Error is already handled in AppContext with toast
    }
  };

  const getStatusBadge = (status: string) => {
    return (
      <Badge variant="outline" className={getStatusBadgeClasses(status)}>
        {getStatusLabel(status)}
      </Badge>
    );
  };

  // Loading skeleton
  if (appointmentsLoading && appointments.length === 0) {
    return (
      <div className="space-y-6 w-full overflow-x-hidden">
        <CommonHeader
          title="Appointment Management"
          description="Schedule and manage appointments"
          onClick={() => navigate("/appointments/new")}
          buttonText="New Appointment"
          buttonIcon={Plus}
        />
        <div className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Appointment Management
          </h1>
          <p className="text-muted-foreground">
            Schedule and manage appointments
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={showAllAppointments ? "default" : "outline"}
            onClick={() => {
              if (!showAllAppointments) {
                // Currently showing 3 days, switch to show all
                setFilters({
                  ...filters,
                  startDate: undefined,
                  endDate: undefined,
                });
              } else {
                // Currently showing all, switch to 3 days
                const dates = getDefaultDateFilter();
                setFilters({
                  ...filters,
                  startDate: dates.startDate,
                  endDate: dates.endDate,
                });
              }
            }}
          >
            {showAllAppointments ? "Showing All" : "Next 3 Days"}
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={refreshAppointments}
            disabled={appointmentsLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${appointmentsLoading ? "animate-spin" : ""}`}
            />
          </Button>
          <Button onClick={() => navigate("/appointments/new")}>
            <Plus className="h-4 w-4 mr-2" />
            New Appointment
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {appointmentsError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>{appointmentsError}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={refreshAppointments}
              disabled={appointmentsLoading}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="calendar">Calendar View</TabsTrigger>
          <TabsTrigger value="list">List View</TabsTrigger>
        </TabsList>

        {/* Calendar View */}
        <TabsContent value="calendar" className="space-y-4">
          {appointmentsLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-96 w-full" />
            </div>
          ) : (
            <AppointmentCalendarView
              currentMonth={currentMonth}
              onMonthChange={setCurrentMonth}
              calendarDays={calendarDays}
              appointments={appointments}
              customers={customers}
              onAppointmentClick={setSelectedAppointment}
            />
          )}
        </TabsContent>

        {/* List View */}
        <TabsContent value="list" className="space-y-4">
          {/* Filters */}
          <AppointmentFilter
            filterStatus={filters.status}
            filters={filters}
            onFilterChange={setFilters}
            customers={customers}
            staff={staff}
          />

          {/* Appointments Table */}
          {appointmentsLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-12 w-full" />
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <AppointmentsTable
              appointments={appointments}
              customers={customers}
              vehicles={vehicles}
              staff={staff}
              getStatusBadge={getStatusBadge}
              onStatusUpdate={handleStatusUpdate}
              onBulkAssignStaff={bulkAssignStaff}
              onView={setSelectedAppointment}
              isLoading={appointmentsLoading}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Appointment Detail Dialog */}
      <AppointmentDetailDialog
        open={!!selectedAppointment}
        onOpenChange={(open) => !open && setSelectedAppointment(null)}
        appointment={selectedAppointment}
        customers={customers}
        vehicles={vehicles}
        staff={staff}
        getStatusBadge={getStatusBadge}
      />
    </div>
  );
}
